import {
  BASE_PATH,
  NAME_MAX_LENGTH,
  PLAYER_COLORS,
  rank,
  sanitizeName,
  WORD_MS,
  type RankingEntry,
  type WordView,
  type ErrorCode,
  type PlayerInfo,
  type RoomStatus,
  type ServerMsg,
} from "../src/protocol";
import type { Lang } from "../src/languages";
import { track } from "./analytics";
import { contrastToggle, GuessGrid, Keyboard, miniRows } from "./board";
import { openDialog } from "./dialog";
import { h } from "./dom";
import { Draft } from "./draft";
import { brandMark, siteFooter } from "./home";
import { copyIcon, exitIcon, shareIcon } from "./icons";
import { uiLangSelect } from "./langPicker";
import { Connection, type ConnectionStatus } from "./net";
import { qrSvg } from "./qr";
import { shareButton, type ResultCard } from "./shareCard";
import { getName, getPlayerId, setName } from "./storage";
import { STRINGS } from "./strings";
import { formatClock } from "./timing";

const CANONICAL_ORIGIN = "https://workbrawl.com";
const TOAST_MS = 2500;
/** The clock is green until this much time is left, then shifts gradually towards red… */
const CLOCK_WARN_MS = 30_000;
/** …which it reaches here: the last seconds are solid red and blink. */
const CLOCK_FINAL_MS = 10_000;
const CLOCK_GREEN_HUE = 135;
/** How often the clock refreshes. The server sends no ticks. */
const REFRESH_MS = 100;
/** The word bar turns amber, then red, in the last seconds of a word. */
const WORD_WARN_MS = 20_000;
const WORD_FINAL_MS = 10_000;

/** Room screen. Asks for a name first when the visitor arrived by link without one. */
export function renderRoom(root: HTMLElement, code: string): void {
  const name = sanitizeName(getName());
  if (name === null) renderNamePrompt(root, code);
  else new RoomScreen(root, code, name);
}

function renderNamePrompt(root: HTMLElement, code: string): void {
  const input = h("input", {
    id: "name",
    type: "text",
    autocomplete: "nickname",
    maxlength: NAME_MAX_LENGTH,
    placeholder: STRINGS.namePlaceholder,
    required: true,
  });
  const error = h("p", { class: "form-error", role: "alert" });
  const form = h(
    "form",
    { class: "card name-prompt", novalidate: true },
    h("h2", {}, STRINGS.namePromptTitle),
    h("p", { class: "muted" }, STRINGS.namePromptBody),
    h("label", { for: "name" }, STRINGS.nameLabel),
    input,
    h("button", { type: "submit", class: "btn btn-primary" }, STRINGS.enterRoom),
    error,
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const name = sanitizeName(input.value);
    if (name === null) {
      error.textContent = STRINGS.nameInvalid;
      input.focus();
      return;
    }
    setName(name);
    new RoomScreen(root, code, name);
  });
  root.replaceChildren(
    h("main", { class: "center-screen" }, h("h1", { class: "brand brand-large" }, brandMark()), form),
    siteFooter(),
  );
  input.focus();
}

class RoomScreen {
  private readonly connection: Connection;
  private readonly toastEl = h("div", { class: "toast", role: "status", "aria-live": "polite", hidden: true });
  private readonly clockEl = h("strong", { class: "clock", "aria-label": STRINGS.timeLeft }, "0:00");
  private readonly headingEl = h("span", { class: "muted" }, STRINGS.timeLeft);
  private readonly wordClockEl = h("span", { class: "word-clock", title: STRINGS.wordLeft });
  /** Time left for the current word (each word has its own limit, apart from the round's clock). */
  private readonly wordBarFill = h("span", { class: "word-bar-fill" });
  private readonly wordBarEl = h(
    "div",
    { class: "word-bar", role: "progressbar", "aria-label": STRINGS.wordLeft, hidden: true },
    this.wordBarFill,
  );
  private readonly statusEl = h("p", { class: "stage-status", "aria-live": "polite" });
  private readonly grid = new GuessGrid((column) => this.select(column));
  private keyboard: Keyboard | null = null;
  private readonly keyboardSlot = h("div", { class: "keyboard-slot" });
  private readonly overlayEl = h("div", { class: "arena-overlay" });
  private readonly playersEl = h("ol", { class: "player-list" });

  private you = "";
  private status: RoomStatus = "lobby";
  private lang: Lang = "en";
  private players = new Map<string, PlayerInfo>();
  private ranking: RankingEntry[] = [];
  /** Round length of the room, for the shared result. */
  private roundSeconds = 0;
  private word: WordView | null = null;
  /** Local time at which the current word (or its reveal) ends. */
  private wordDeadline = 0;
  /** Letters typed for the next guess. */
  private readonly draft = new Draft();
  /** A guess sent and not answered yet: typing waits for it. */
  private pending = false;
  /** Local time at which the countdown or the round ends. */
  private deadline = 0;
  private refreshTimer: ReturnType<typeof setInterval> | undefined;
  private toastTimer: ReturnType<typeof setTimeout> | undefined;
  private fatal = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly code: string,
    name: string,
  ) {
    const stage = h(
      "div",
      { class: "card stage" },
      this.grid.el,
      this.statusEl,
      this.keyboardSlot,
      contrastToggle(),
      this.overlayEl,
    );

    root.replaceChildren(
      this.header(),
      this.toastEl,
      h(
        "main",
        { class: "arena-layout" },
        h(
          "section",
          { class: "arena-area" },
          h("div", { class: "arena-hud" }, h("span", {}, this.headingEl, " ", this.wordClockEl), this.clockEl),
          this.wordBarEl,
          stage,
        ),
        h(
          "aside",
          { class: "panel" },
          h("section", { class: "card" }, h("h3", { class: "panel-title" }, STRINGS.scoreboard), this.playersEl),
          this.inviteCard(),
        ),
      ),
      siteFooter(),
    );

    this.connection = new Connection(Connection.roomUrl(code, getPlayerId(), name), {
      onMessage: (msg) => this.onMessage(msg),
      onStatus: (status) => this.onStatus(status),
    });
    this.connection.connect();
  }

  private header(): HTMLElement {
    const exit = h(
      "button",
      { type: "button", class: "btn btn-small btn-exit", "aria-haspopup": "dialog", "aria-label": STRINGS.exit },
      exitIcon(),
      // Hidden on narrow screens, where the button is icon-only.
      h("span", { class: "btn-label" }, STRINGS.exit),
    );
    exit.addEventListener("click", () => this.confirmExit());
    return h(
      "header",
      { class: "topbar" },
      h("a", { href: `${BASE_PATH}/`, class: "brand" }, brandMark()),
      h(
        "div",
        { class: "room-info" },
        h("span", { class: "muted room-label" }, STRINGS.room),
        h("strong", { class: "room-code" }, this.code),
        uiLangSelect(true),
        exit,
      ),
    );
  }

  private confirmExit(): void {
    const cancel = h("button", { type: "button", class: "btn" }, STRINGS.cancel);
    const leave = h("button", { type: "button", class: "btn btn-danger" }, exitIcon(), STRINGS.exit);
    const dialog = openDialog(
      "confirm",
      h("h2", { class: "dialog-title" }, STRINGS.exitTitle),
      h("p", { class: "muted" }, STRINGS.exitBody),
      h("div", { class: "dialog-actions" }, cancel, leave),
    );
    cancel.addEventListener("click", () => dialog.close());
    leave.addEventListener("click", () => {
      // Suppress the "connection lost" message while we leave on purpose.
      this.fatal = true;
      this.connection.stop();
      location.assign(`${BASE_PATH}/`);
    });
    cancel.focus();
  }

  /** Copies the invite link; reports through a floating message unless `onDone` handles it. */
  private async copyInvite(onDone?: (ok: boolean) => void): Promise<void> {
    const url = inviteUrl(this.code);
    const ok = await copyText(url);
    if (onDone) onDone(ok);
    else this.toast(ok ? STRINGS.linkCopiedToast : `${STRINGS.copyFailed} ${url}`);
  }

  /** Invite card under the scoreboard: title with share/copy actions on the right. */
  private inviteCard(): HTMLElement {
    const share = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.share, "aria-label": STRINGS.share, "aria-haspopup": "dialog" },
      shareIcon(),
    );
    share.addEventListener("click", () => this.openInvite());
    const copy = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.copyLink, "aria-label": STRINGS.copyLink },
      copyIcon(),
    );
    copy.addEventListener("click", () => this.copyInvite());

    return h(
      "section",
      { class: "card invite-card" },
      h(
        "div",
        { class: "invite-head" },
        h("h3", { class: "panel-title" }, STRINGS.inviteTitle),
        h("div", { class: "icon-actions" }, share, copy),
      ),
    );
  }

  /** Modal with a large QR, the link, copy and (where supported) the native share sheet. */
  private openInvite(): void {
    const url = inviteUrl(this.code);
    const copy = h("button", { type: "button", class: "btn" }, copyIcon(), STRINGS.copyLink);
    copy.addEventListener("click", () =>
      this.copyInvite((ok) => {
        copy.replaceChildren(copyIcon(), ok ? STRINGS.linkCopied : STRINGS.copyFailedShort);
        setTimeout(() => copy.replaceChildren(copyIcon(), STRINGS.copyLink), 1500);
      }),
    );
    const close = h("button", { type: "button", class: "btn" }, STRINGS.close);

    const actions: HTMLElement[] = [];
    if (typeof navigator.share === "function") {
      const native = h("button", { type: "button", class: "btn btn-primary" }, shareIcon(), STRINGS.share);
      native.addEventListener("click", () => {
        navigator.share({ title: STRINGS.appName, text: STRINGS.shareText, url }).catch(() => {
          // The share sheet was dismissed: nothing to do.
        });
      });
      actions.push(native);
    }
    actions.push(copy, close);

    const dialog = openDialog(
      "invite",
      h("h2", { class: "dialog-title" }, STRINGS.inviteTitle),
      h("div", { class: "qr-box qr-box-large" }, qrSvg(url, STRINGS.qrLabel(this.code))),
      h("p", { class: "invite-url" }, url),
      h("div", { class: "dialog-actions" }, ...actions),
    );
    close.addEventListener("click", () => dialog.close());
  }

  /** The keyboard follows the language of the room's words (Spanish adds Ñ). */
  private ensureKeyboard(): void {
    if (this.keyboard && this.keyboard.el.dataset.lang === this.lang) return;
    this.keyboard = new Keyboard(this.lang, (key) => this.onKey(key));
    this.keyboard.el.dataset.lang = this.lang;
    this.keyboardSlot.replaceChildren(this.keyboard.el);
  }

  private canType(): boolean {
    return this.status === "running" && this.word?.phase === "open" && !this.word.mine.done && !this.pending;
  }

  private onKey(key: string): void {
    if (!this.canType()) return;
    if (key === "backspace") this.draft.backspace();
    else if (key === "left") this.draft.move(-1);
    else if (key === "right") this.draft.move(1);
    else if (key === "enter") {
      if (!this.draft.complete) {
        this.toast(STRINGS.tooShort);
        this.grid.shake(this.word!.mine.guesses.length);
        return;
      }
      this.pending = this.connection.send({ type: "guess", word: this.draft.word });
      return;
    } else this.draft.type(key);
    this.renderWord();
  }

  /** A click on a tile of the row being typed: the next letter goes there. */
  private select(column: number): void {
    if (!this.canType()) return;
    this.draft.select(column);
    this.renderWord();
  }

  private onStatus(status: ConnectionStatus): void {
    if (this.fatal) return;
    if (status === "connecting") this.toast(STRINGS.connecting, true);
    else if (status === "reconnecting") this.toast(STRINGS.reconnecting, true);
    else if (status === "open") this.hideToast();
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.type) {
      case "state": {
        const previous = this.status;
        this.you = msg.you;
        this.status = msg.status;
        this.roundSeconds = msg.duration;
        // One per person per round: this is what "how much is each title used" is measured by.
        if (msg.status === "running" && previous !== "running") {
          track("round_started", { people: msg.players.length, duration: msg.duration });
        } else if (msg.status === "finished" && previous === "running") {
          track("round_finished", { people: msg.players.length });
        }
        this.lang = msg.lang;
        this.ensureKeyboard();
        this.players = new Map(msg.players.map((p) => [p.id, p]));
        this.ranking = msg.ranking;
        this.deadline = Date.now() + msg.remainingMs;
        if (msg.status !== "running") this.word = null;
        this.renderStatus();
        this.renderWord();
        this.renderTeam();
        break;
      }
      case "word": {
        for (const [slot, score] of msg.p) {
          for (const player of this.players.values()) if (player.slot === slot) player.score = score;
        }
        const previous = this.word;
        const newWord = !previous || previous.n !== msg.n;
        const newGuess = !newWord && msg.mine.guesses.length > previous.mine.guesses.length;
        if (newWord || newGuess) {
          this.draft.clear();
          this.pending = false;
        }
        if (newGuess && msg.mine.solved) navigator.vibrate?.(30);
        this.word = msg;
        this.deadline = Date.now() + msg.t;
        this.wordDeadline = Date.now() + msg.wordMs;
        this.renderWord(newGuess);
        this.renderTeam();
        this.refresh();
        break;
      }
      case "invalid":
        this.pending = false;
        this.toast(msg.error === "NOT_A_WORD" ? STRINGS.notAWord : STRINGS.tooShort);
        if (this.word) this.grid.shake(this.word.mine.guesses.length);
        break;
      case "player_joined":
      case "player_updated":
        this.players.set(msg.player.id, msg.player);
        this.renderTeam();
        if (this.status === "lobby") this.renderStatus();
        break;
      case "player_left":
        this.players.delete(msg.id);
        this.renderTeam();
        if (this.status === "lobby") this.renderStatus();
        break;
      case "error":
        this.onError(msg.code);
        break;
      case "pong":
        break;
    }
  }

  private onError(code: ErrorCode): void {
    if (code === "BAD_MESSAGE") return;
    this.fatal = true;
    this.connection.stop();
    clearInterval(this.refreshTimer);
    this.root.replaceChildren(
      h(
        "main",
        { class: "center-screen" },
        h("h1", { class: "brand brand-large" }, brandMark()),
        h(
          "section",
          { class: "card center-card" },
          h("p", {}, code === "ROOM_FULL" ? STRINGS.errorRoomFull : STRINGS.errorRoomNotFound),
          h("a", { href: `${BASE_PATH}/`, class: "btn btn-primary" }, STRINGS.backHome),
        ),
      ),
      siteFooter(),
    );
  }

  /** Overlay and timers for the current phase of the room. */
  private renderStatus(): void {
    clearInterval(this.refreshTimer);
    this.refreshTimer = undefined;
    this.overlayEl.hidden = this.status === "running";
    // The word bar only runs with the round; hide it right away when the round stops.
    this.renderWordBar(Date.now());

    if (this.status === "lobby") {
      this.setClock(0);
      this.overlayEl.replaceChildren(
        h("h2", {}, STRINGS.lobbyTitle),
        h("p", {}, STRINGS.lobbyBody(this.onlineCount())),
        this.startButton(STRINGS.startRound),
      );
    } else if (this.status === "countdown") {
      const number = h("strong", { class: "countdown" });
      const update = () => {
        const next = String(Math.max(1, Math.ceil((this.deadline - Date.now()) / 1000)));
        if (number.textContent === next) return;
        number.textContent = next;
        // Restart the zoom-out for each new number (reading offsetWidth forces a reflow).
        number.classList.remove("countdown-pop");
        void number.offsetWidth;
        number.classList.add("countdown-pop");
      };
      update();
      this.refreshTimer = setInterval(update, REFRESH_MS);
      this.overlayEl.replaceChildren(h("p", {}, STRINGS.getReady), number);
    } else if (this.status === "finished") {
      this.setClock(0);
      this.overlayEl.replaceChildren(...this.resultsContent());
    } else {
      this.refresh();
      this.refreshTimer = setInterval(() => this.refresh(), REFRESH_MS);
    }
  }

  private resultsContent(): HTMLElement[] {
    const [first, second] = this.ranking;
    const headline = !first ? "" : second && second.score === first.score ? STRINGS.tie : first.id === this.you ? STRINGS.youWin : STRINGS.winner(first.name);
    return [
      h("h2", {}, STRINGS.resultsTitle),
      h("p", { class: "results-headline" }, headline),
      h(
        "ol",
        { class: "ranking" },
        // The podium only: the full order is in the scoreboard next to the board.
        ...this.ranking.slice(0, 3).map((entry, i) => {
          const row = h(
            "li",
            { class: i === 0 ? "ranking-row ranking-first" : "ranking-row" },
            h("span", { class: "ranking-pos" }, `${i + 1}`),
            h("span", { class: "avatar", "aria-hidden": "true" }, initial(entry.name)),
            h("span", { class: "player-name" }, entry.name),
            h("span", { class: "player-score" }, STRINGS.points(entry.score)),
          );
          row.style.setProperty("--player", PLAYER_COLORS[entry.slot % PLAYER_COLORS.length]!);
          return row;
        }),
      ),
      this.startButton(STRINGS.newRound),
      shareButton(() => this.resultCard()),
    ];
  }

  /** What "Share result" draws: the podium, worded for people who weren't in the room. */
  private resultCard(): ResultCard {
    const [first, second] = this.ranking;
    return {
      title: "GuessBrawl",
      slug: "guessbrawl",
      accent: "#1f2a44",
      headline: !first ? STRINGS.resultsTitle : second && second.score === first.score ? STRINGS.tie : STRINGS.winner(first.name),
      podium: this.ranking.slice(0, 3).map((entry) => ({
        name: entry.name,
        score: STRINGS.points(entry.score),
        color: PLAYER_COLORS[entry.slot % PLAYER_COLORS.length]!,
      })),
      people: this.ranking.length || this.players.size,
      minutes: Math.round(this.roundSeconds / 60),
    };
  }

  private startButton(label: string): HTMLElement {
    const button = h("button", { type: "button", class: "btn btn-primary" }, label);
    button.addEventListener("click", () => this.connection.send({ type: "start" }));
    return button;
  }

  /** My board, the keyboard colors and the line under the board. */
  private renderWord(animate = false): void {
    const word = this.word;
    this.ensureKeyboard();
    if (!word) {
      this.headingEl.textContent = STRINGS.timeLeft;
      this.wordClockEl.textContent = "";
      this.statusEl.textContent = "";
      this.grid.render([], [], null);
      this.keyboard!.color([], []);
      this.keyboard!.setEnabled(false);
      return;
    }
    const { mine } = word;
    this.headingEl.textContent = STRINGS.wordNumber(word.n);
    this.grid.render(mine.guesses, mine.marks, mine.done || word.phase !== "open" ? null : this.draft, animate);
    this.keyboard!.color(mine.guesses, mine.marks);
    this.keyboard!.setEnabled(this.canType());

    const nameOf = (slot: number) => [...this.players.values()].find((p) => p.slot === slot)?.name ?? "";
    let status = "";
    if (word.phase === "reveal") {
      status = STRINGS.answerWas(word.answer!.toUpperCase());
      // Not done yet means the word's own time ran out (the round's clock may still be going).
      if (!mine.done) status = `${STRINGS.wordTimeUp} · ${status}`;
      if (word.first >= 0) status += ` · ${STRINGS.firstSolved(nameOf(word.first))}`;
    } else if (mine.solved) status = `${STRINGS.solvedMine(mine.guesses.length)} ${STRINGS.waitingOthers}`;
    else if (mine.done) status = `${STRINGS.outOfTries}. ${STRINGS.waitingOthers}`;
    this.statusEl.textContent = status;
    this.statusEl.classList.toggle("is-solved", mine.solved);
  }

  private renderTeam(): void {
    this.playersEl.replaceChildren(
      ...rank([...this.players.values()]).map((p) => {
        const row = h(
          "li",
          { class: p.online ? "player" : "player player-offline" },
          h("span", { class: "avatar", "aria-hidden": "true" }, initial(p.name)),
          h(
            "span",
            { class: "player-name" },
            p.name,
            p.id === this.you ? h("span", { class: "tag" }, STRINGS.you) : null,
            p.online ? null : h("span", { class: "tag" }, STRINGS.offline),
          ),
          h("span", { class: "player-score", title: STRINGS.points(p.score) }, String(p.score)),
          // Their progress on the current word: colors only, never letters.
          this.progressOf(p),
        );
        row.style.setProperty("--player", PLAYER_COLORS[p.slot % PLAYER_COLORS.length]!);
        return row;
      }),
    );
  }

  private progressOf(p: PlayerInfo): HTMLElement | null {
    const word = this.word;
    if (!word) return null;
    if (p.id === this.you) return miniRows(word.mine.marks);
    const other = word.others.find(([slot]) => slot === p.slot);
    return other ? miniRows(other[1]) : null;
  }

  /** Round clock, from the local deadline. */
  private refresh(): void {
    const now = Date.now();
    this.setClock(this.deadline - now);
    this.wordClockEl.textContent = this.word ? `· ${formatClock(this.wordDeadline - now)}` : "";
    this.renderWordBar(now);
  }

  /** The word's own time limit, as a bar that turns amber and then red near the end. */
  private renderWordBar(now: number): void {
    const open = this.status === "running" && this.word?.phase === "open";
    this.wordBarEl.hidden = !open;
    if (!open) return;
    const left = Math.max(0, this.wordDeadline - now);
    this.wordBarFill.style.width = `${(left / WORD_MS) * 100}%`;
    this.wordBarEl.classList.toggle("is-low", left <= WORD_WARN_MS && left > WORD_FINAL_MS);
    this.wordBarEl.classList.toggle("is-final", left <= WORD_FINAL_MS);
    this.wordBarEl.setAttribute("aria-valuetext", formatClock(left));
  }

  private onlineCount(): number {
    return [...this.players.values()].filter((p) => p.online).length;
  }

  private setClock(ms: number): void {
    this.clockEl.textContent = formatClock(ms);
    const running = this.status === "running";
    this.clockEl.classList.toggle("clock-running", running);
    this.clockEl.classList.toggle("clock-final", running && ms <= CLOCK_FINAL_MS);
    // Hue 135 (green) → 0 (red), passing through yellow and orange on the way.
    const left = Math.min(1, Math.max(0, (ms - CLOCK_FINAL_MS) / (CLOCK_WARN_MS - CLOCK_FINAL_MS)));
    this.clockEl.style.setProperty("--clock-hue", String(Math.round(CLOCK_GREEN_HUE * left)));
  }

  private toast(text: string, sticky = false): void {
    clearTimeout(this.toastTimer);
    this.toastEl.textContent = text;
    this.toastEl.hidden = false;
    if (!sticky) this.toastTimer = setTimeout(() => this.hideToast(), TOAST_MS);
  }

  private hideToast(): void {
    clearTimeout(this.toastTimer);
    this.toastEl.hidden = true;
  }
}

/** Clipboard API where available, with the legacy selection path as a fallback. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  const area = h("textarea", { readonly: true, class: "visually-hidden" });
  area.value = text;
  document.body.append(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}

function initial(name: string): string {
  return [...name][0]?.toUpperCase() ?? "?";
}

/**
 * Invite links use the canonical domain, except when running locally or on
 * *.workers.dev, where the canonical link would point to a different deployment.
 */
function inviteUrl(code: string): string {
  const host = location.hostname;
  const isLocal =
    host === "localhost" ||
    /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) ||
    host.endsWith(".workers.dev");
  return `${isLocal ? location.origin : CANONICAL_ORIGIN}${BASE_PATH}/r/${code}`;
}
